import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import GenericModal from "../generic_modal";
import {
    findStepById,
    findStepByLocation,
} from "../../config/tourSteps";
import { useTourGuiado } from "../../global/tourGuiadoContext";
import { useImpersonation } from "../../global/impersonation";
import { isTourGuiadoEnabled } from "../../config/featureFlags";

// Modal explicativo curto que abre na primeira visita a cada uma das 6 areas
// quando o tour esta ativo. Ao fechar, marca hint_seen_at (idempotente).
//
// Quando o switch reativa o tour, hint_seen_at e zerado e este modal volta
// a abrir nas proximas visitas (D6). completed_at NAO e tocado pelo PageHint
// — o Wrapper cuida de marcar concluido ao acessar a rota-step (D3).
//
// Modo review (always_show_hints=true): apos reativacao pos-conclusao, o gate
// de hint_seen_at e ignorado e o modal abre toda vez que o user visita a
// rota-step.

export default function PageHint({ stepId }) {
    const location = useLocation();
    const { isImpersonate } = useImpersonation();
    const { state, hydrated, markStepHintSeen } = useTourGuiado();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (isImpersonate) return;
        if (!hydrated) return;
        if (!isTourGuiadoEnabled()) return;
        if (!state.tour_active) {
            setOpen(false);
            return;
        }
        if (
            !state.always_show_hints &&
            state.progress?.[stepId]?.hint_seen_at
        ) {
            return;
        }
        const matched = findStepByLocation(
            location.pathname,
            location.search
        );
        if (matched && matched.id === stepId) {
            setOpen(true);
        } else {
            // Saiu da rota-step (ex: navegou entre abas de settings) — fecha
            // o modal anterior para nao ficar empilhado em cima do proximo.
            setOpen(false);
        }
    }, [
        location.pathname,
        location.search,
        isImpersonate,
        hydrated,
        state.tour_active,
        state.always_show_hints,
        state.progress,
        stepId,
    ]);

    if (!open) return null;

    const step = findStepById(stepId);
    if (!step) return null;

    const handleClose = () => {
        setOpen(false);
        // Em modo review, hint_seen_at e ignorado pelo gate. Marcar de novo
        // dispararia o useEffect (state.progress mudou) e, sem o gate de
        // hint_seen_at bloqueando, o modal reabriria imediatamente. Skip.
        if (!state.always_show_hints) {
            markStepHintSeen(stepId);
        }
    };

    return (
        <GenericModal
            open={open}
            onClose={handleClose}
            title={step.title}
            width={480}
            showClose={true}
        >
            <Body>{step.description}</Body>
            <Actions>
                <PrimaryButton type="button" onClick={handleClose}>
                    Entendi
                </PrimaryButton>
            </Actions>
        </GenericModal>
    );
}

const Body = styled.p`
    font-family: "Montserrat", sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #181122;
    margin: 0 0 16px 0;
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const PrimaryButton = styled.button`
    background: #57c4c2;
    color: #ffffff;
    border: none;
    border-radius: 4px;
    padding: 10px 20px;
    font-family: "Montserrat", sans-serif;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.2s;
    &:hover {
        background: #4ba09b;
    }
`;
